import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandSymbol from '../ui/BrandSymbol';
import { ResponsiveContainer, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';

const Frame123: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();
  const [selection, setSelection] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<typeof categories[0] | null>(null);
  
  // Load saved selection from session
  useEffect(() => {
    const savedProgress = session.taskProgress['frame-123'];
    if (savedProgress?.answers?.selection) {
      setSelection(savedProgress.answers.selection);
      const category = categories.find(c => c.id === savedProgress.answers.selection);
      if (category) {
        setSelectedCategory(category);
      }
    }
  }, [session]);

  const categories = [
    {
      id: 'have-will',
      label: 'I already have a last will & testament',
      headline: "Having a will is a strong first step. We'll help you round out your estate plan with other essentials—personal, legal, and financial—that ensure nothing gets missed."
    },
    {
      id: 'thought-about-it',
      label: "I don't have a will yet, but I've thought about how I'd like to be remembered",
      headline: "That's a meaningful place to begin. We'll help you turn your wishes into a plan that protects your loved ones and your legacy."
    },
    {
      id: 'dont-know',
      label: "I don't know where to start",
      headline: "Starting is the hardest part, but that's what we're here for. We'll break things down into clear, simple steps no prep needed."
    }
  ];

  const handleNext = async () => {
    if (selection) {
      // Save selection to session
      await updateTaskProgress('frame-123', {
        selection
      }, 'complete');
    }
    navigate('/frame-126');
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
      {/* Bottom overlay elements per Figma */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0">
        <div className="h-32 bg-stone-50" />
        <div className="h-14 -mt-14 bg-gradient-to-b from-stone-50/0 to-stone-50" />
      </div>

      {/* Centered dark logo with hyperlink */}
        <div className="flex flex-col items-center w-full">
          <a 
            href={import.meta.env.VITE_ENVIRONMENT === 'preprod' 
              ? 'https://preprod.endofanera.ai/' 
              : 'https://my.endofanera.ai/'}
            className="hover:opacity-80 transition-opacity duration-200"
          >
            <BrandSymbol size="3xl" showText={true} variant="dark" className="mx-auto mb-8 sm:mb-10 lg:mb-12" />
          </a>
            
      {/* Content text block centered */}
          <div className="flex flex-col items-start w-full max-w-xs sm:max-w-md lg:max-w-2xl xl:max-w-4xl mx-auto">
            <h2 className="font-display text-lg sm:text-xl lg:text-2xl font-semibold text-green-700 mb-4 sm:mb-5 lg:mb-6 leading-relaxed w-full">
              {selectedCategory ? selectedCategory.headline : "Planning for your loved ones is important, but the process can be daunting. End of an Era is here to help."}
            </h2>

            <p className="font-sans text-base sm:text-lg font-medium text-stone-800 mb-6 sm:mb-8 lg:mb-10 w-full">Which best describes your situation?</p>

            <form className="space-y-4 sm:space-y-5 lg:space-y-6 w-full ml-2 sm:ml-3">
          {categories.map((category) => (
            <label key={category.id} className="flex items-center space-x-3 cursor-pointer select-none">
              <input
                type="radio"
                name="category"
                value={category.id}
                checked={selection === category.id}
                onChange={() => {
                  setSelection(category.id);
                  setSelectedCategory(category);
                  // Auto-save selection on change
                  updateTaskProgress('frame-123', { 
                    selection: category.id,
                    label: category.label,
                    headline: category.headline
                  }, 'in-progress');
                }}
                className="form-radio h-4 w-4 text-cyan-700 focus:ring-cyan-700 flex-shrink-0"
              />
              <span className="font-sans text-sm sm:text-base text-stone-800 leading-relaxed">{category.label}</span>
            </label>
          ))}
        </form>
          </div>

          {/* Bottom spacer to ensure footer is always accessible */}
          <div className="h-20 sm:h-32 lg:h-40" />
        </div>
      </ResponsiveContent>

      {/* ResponsiveFooter without back button */}
      <ResponsiveFooter
        title="Welcome"
        subtitle="Getting started with your estate plan"
        progressPercent={40}
        onNext={handleNext}
        nextDisabled={!selection}
        nextText="Next →"
      />
    </ResponsiveContainer>
  );
};

export default Frame123;